import React, { useCallback, useEffect, useState } from 'react';
import { Check, Inbox, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DashboardHeader } from '../Dashboard/DashboardUi';
import { Modal } from '../ui/Modal';
import { Field, Textarea } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { demandesModulesApi, messageErreur, type DemandeModule } from '../../lib/organisationApi';
import { EtatEchec, Squelettes } from './EtatsPage';

/**
 * Demandes d'activation de module (super-admin) : un client demande un module, L'IAmani accepte (le module est
 * activé pour le service) ou refuse (avec un mot facultatif que le client voit sur la carte du module).
 */

const dateCourte = (iso: string) => {
    const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};

export const DemandesModulesPage: React.FC = () => {
    const [demandes, setDemandes] = useState<DemandeModule[] | null>(null);
    const [erreur, setErreur] = useState<unknown>(null);
    const [info, setInfo] = useState<{ ok: boolean; texte: string } | null>(null);
    const [enCours, setEnCours] = useState<string | null>(null);
    const [aRefuser, setARefuser] = useState<DemandeModule | null>(null);
    const [reponse, setReponse] = useState('');

    const charger = useCallback(async () => {
        setErreur(null);
        try { setDemandes((await demandesModulesApi.lister()).demandes); } catch (e) { setErreur(e); }
    }, []);
    useEffect(() => { void charger(); }, [charger]);

    const accepter = async (d: DemandeModule) => {
        setEnCours(d.id); setInfo(null);
        try {
            await demandesModulesApi.accepter(d.id);
            setInfo({ ok: true, texte: `« ${d.module_label} » est activé pour ${d.etablissement || 'ce client'} (${d.service}).` });
            await charger();
        } catch (e) { setInfo({ ok: false, texte: messageErreur(e) }); }
        finally { setEnCours(null); }
    };

    const refuser = async () => {
        if (!aRefuser) return;
        setEnCours(aRefuser.id); setInfo(null);
        try {
            await demandesModulesApi.refuser(aRefuser.id, reponse);
            setInfo({ ok: true, texte: `Demande refusée pour « ${aRefuser.module_label} ».` });
            setARefuser(null); setReponse('');
            await charger();
        } catch (e) { setInfo({ ok: false, texte: messageErreur(e) }); }
        finally { setEnCours(null); }
    };

    return (
        <div className="space-y-4 max-w-[900px]">
            <DashboardHeader societe="" titre="Demandes de modules" sousTitre={demandes ? `${demandes.length} demande${demandes.length > 1 ? 's' : ''} en attente` : ' '} />
            {info && <div role={info.ok ? 'status' : 'alert'} className={`rounded-xl px-4 py-3 text-[13px] ${info.ok ? 'bg-state-ok-soft text-state-ok' : 'bg-state-danger-soft text-state-danger'}`}>{info.texte}</div>}
            {erreur ? <EtatEchec erreur={erreur} titre="Impossible d'afficher les demandes" onRetry={() => charger()} /> : !demandes ? <Squelettes lignes={3} /> : demandes.length === 0 ? (
                <div className="rounded-xl border border-border bg-surface px-5 py-10 text-center">
                    <Inbox size={22} className="mx-auto text-content-muted mb-2" />
                    <div className="text-[14px] font-semibold text-content">Aucune demande en attente</div>
                    <p className="text-[12.5px] text-content-muted mt-1">Quand un client demandera l'activation d'un module, elle apparaîtra ici.</p>
                </div>
            ) : (
                <ul className="rounded-xl border border-border bg-surface divide-y divide-border-soft">
                    {demandes.map((d) => (
                        <li key={d.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                            <div className="min-w-0 flex-1 basis-[240px]">
                                <div className="text-[13.5px] font-semibold text-content">{d.module_label}</div>
                                <div className="text-[12px] text-content-muted">
                                    <Link to={`/admin/clients/${d.client_id}/organisation`} className="underline hover:text-content">{d.etablissement || d.client_id}</Link>
                                    {d.service ? ` · ${d.service}` : ''} · {dateCourte(d.created_at)}
                                </div>
                                {d.message && <p className="mt-1.5 text-[12.5px] text-content rounded-lg bg-surface-soft px-3 py-2">« {d.message} »</p>}
                            </div>
                            <div className="flex gap-2">
                                <button type="button" className={pillSecondary(36)} disabled={enCours === d.id} onClick={() => { setARefuser(d); setReponse(''); }}><X size={14} /> Refuser</button>
                                <button type="button" className={pillPrimary(36)} disabled={enCours === d.id} onClick={() => void accepter(d)}><Check size={14} /> Accepter</button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <Modal
                open={!!aRefuser} onClose={() => setARefuser(null)} size="md" title={aRefuser ? `Refuser « ${aRefuser.module_label} » ?` : ''} subtitle={aRefuser ? `${aRefuser.etablissement} · ${aRefuser.service}` : undefined}
                footer={<><button type="button" className={pillSecondary(36)} onClick={() => setARefuser(null)}>Annuler</button><button type="button" className={pillPrimary(36)} disabled={enCours !== null} onClick={() => void refuser()}>Refuser la demande</button></>}
            >
                <Field label="Réponse au client (facultatif)" counter={`${reponse.length}/300`} help="Le client la verra sur la carte du module.">
                    {(ids) => <Textarea {...ids} value={reponse} maxLength={300} rows={3} onChange={(e) => setReponse(e.target.value)} placeholder="Par exemple : ce module sera proposé le mois prochain." />}
                </Field>
            </Modal>
        </div>
    );
};

export default DemandesModulesPage;
